// @flow
import * as React from 'react';
import {useState} from 'react';

import {capitalize} from 'lodash';
import Button from 'sail/Button';
import Dialog, {DialogContent, DialogFooter, DialogHeader} from 'sail/Dialog';
import {Body, BodyAlt} from 'sail/Text';
import {TextInput} from 'sail/Input';
import Group from 'sail/Group';
import Checkbox from 'sail/Checkbox';
import {useDiagramGenerator} from 'docs/data/diagram';
import Select, {SelectOption} from 'sail/Select';

const EDGE_TYPES = {
  default: 'draws a smooth, curved line.',
  straight: 'draws a straight line without any curves.',
  step: 'draws a line with a sharp 90 degree angle in the middle of the edge.',
  smoothstep:
    'draws a line with a curved 90 degree angle in the middle of the edge.',
  dashed: 'draws a smooth dashed line.',
  raised:
    'draws two 90 degree angles to draw the edge away from the connecting nodes -- good for connecting nodes on the same axis.',
};

type AddEventProps = {|
  event: string,
  setEvent: (string) => void,
|};

const AddEvent = ({event, setEvent}: AddEventProps) => {
  const {type} = useDiagramGenerator();
  if (type !== 'sequence') {
    return null;
  }
  return (
    <Group spacing={4}>
      <label htmlFor="edgeEvent">
        <BodyAlt>Webhook event</BodyAlt>
      </label>
      <TextInput id="edgeEvent" onChange={setEvent} value={event} />
    </Group>
  );
};

type AddStyleProps = {|
  style: string,
  setStyle: (string) => void,
|};

const AddStyle = ({style, setStyle}: AddStyleProps) => {
  const {type, edge} = useDiagramGenerator();

  if (type === 'sequence') {
    return (
      <Checkbox
        checked={style === 'dashed'}
        onChange={(e) => {
          const {target} = e;
          if (target instanceof HTMLInputElement) {
            const {checked} = target;
            setStyle(checked ? 'dashed' : 'default');
          }
        }}
        label="Is response?"
        description="Check this box if this edge represents a response coming from part of a system (rather than a request). Will render as a dashed line."
      />
    );
  } else {
    return (
      <Group spacing={4}>
        <label htmlFor="edgeStyle">
          <BodyAlt>Edge style</BodyAlt>
        </label>

        <Select
          id="edgeStyle"
          onChange={setStyle}
          value={style}
          defaultValue={
            edge.fromPosition === edge.toPosition ? 'raised' : 'default'
          }
        >
          {Object.keys(EDGE_TYPES)
            .sort()
            .map((t) => (
              <SelectOption key={t} label={t} value={t} />
            ))}
        </Select>
        <Body>
          {capitalize(style)} style: {EDGE_TYPES[style]}
        </Body>
      </Group>
    );
  }
};

export const AddEdge = () => {
  const {type, addEdgeContent} = useDiagramGenerator();
  const [label, setLabel] = useState('');
  const [event, setEvent] = useState('');
  const [error, setError] = useState('');
  const [style, setStyle] = useState('default');

  return (
    <Dialog width="medium">
      <DialogHeader title="Add edge content" />
      <DialogContent padding={20}>
        <Group spacing={20}>
          <Group spacing={4}>
            <label htmlFor="edgeLabel">
              <BodyAlt>Edge label</BodyAlt>
            </label>
            <TextInput
              id="edgeLabel"
              onChange={setLabel}
              value={label}
              invalid={!!error}
              placeholder={
                type === 'sequence'
                  ? 'Add a label to the edge'
                  : 'Add an optional label to the edge'
              }
            />
            {error ? <Body>{error}</Body> : ''}
          </Group>
          <AddEvent event={event} setEvent={setEvent} />
          <AddStyle style={style} setStyle={setStyle} />
        </Group>
      </DialogContent>
      <DialogFooter
        end={
          <Button
            label="Add"
            onClick={() => {
              if (type === 'sequence' && !label) {
                setError('A label is required for sequence diagram edges');
                setTimeout(() => {
                  setError('');
                }, 5000);
              } else {
                addEdgeContent({
                  label,
                  event,
                  style,
                });
              }
            }}
          />
        }
      />
    </Dialog>
  );
};
